import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';
import { Calendar, Download, RefreshCw, TrendingUp, TrendingDown, ChevronDown, MapPin, Search, Star, X, Plus, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fetchWeatherForecast } from '../../services/api';
import { WeatherForecast, MarketZone } from '../../types/data';
interface LocationOption {
  name: string;
  code: MarketZone;
  isFavorite?: boolean;
}
const availableLocations: LocationOption[] = [
// Europe - Countries
{
  name: 'Germany',
  code: 'DE'
}, {
  name: 'Spain',
  code: 'ES'
}, {
  name: 'United Kingdom',
  code: 'UK'
}, {
  name: 'France',
  code: 'FR'
}, {
  name: 'Italy',
  code: 'IT'
}, {
  name: 'Netherlands',
  code: 'NL'
}, {
  name: 'Belgium',
  code: 'BE'
}, {
  name: 'Austria',
  code: 'AT'
}, {
  name: 'Switzerland',
  code: 'CH'
}, {
  name: 'Poland',
  code: 'PL'
}, {
  name: 'Sweden',
  code: 'SE'
}, {
  name: 'Norway',
  code: 'NO'
}, {
  name: 'Denmark',
  code: 'DK'
}, {
  name: 'Finland',
  code: 'FI'
}, {
  name: 'Portugal',
  code: 'PT'
}, {
  name: 'Greece',
  code: 'GR'
}, {
  name: 'Czech Republic',
  code: 'CZ'
}, {
  name: 'Ireland',
  code: 'IE'
},
// Europe - Major Cities
{
  name: 'London, UK',
  code: 'GB-LON'
}, {
  name: 'Paris, France',
  code: 'FR-PAR'
}, {
  name: 'Berlin, Germany',
  code: 'DE-BER'
}, {
  name: 'Munich, Germany',
  code: 'DE-MUN'
}, {
  name: 'Hamburg, Germany',
  code: 'DE-HAM'
}, {
  name: 'Madrid, Spain',
  code: 'ES-MAD'
}, {
  name: 'Barcelona, Spain',
  code: 'ES-BCN'
}, {
  name: 'Rome, Italy',
  code: 'IT-ROM'
}, {
  name: 'Milan, Italy',
  code: 'IT-MIL'
}, {
  name: 'Amsterdam, Netherlands',
  code: 'NL-AMS'
}, {
  name: 'Brussels, Belgium',
  code: 'BE-BRU'
}, {
  name: 'Vienna, Austria',
  code: 'AT-VIE'
}, {
  name: 'Zurich, Switzerland',
  code: 'CH-ZRH'
}, {
  name: 'Stockholm, Sweden',
  code: 'SE-STO'
}, {
  name: 'Oslo, Norway',
  code: 'NO-OSL'
}, {
  name: 'Copenhagen, Denmark',
  code: 'DK-CPH'
}, {
  name: 'Helsinki, Finland',
  code: 'FI-HEL'
}, {
  name: 'Lisbon, Portugal',
  code: 'PT-LIS'
}, {
  name: 'Athens, Greece',
  code: 'GR-ATH'
}, {
  name: 'Prague, Czech Republic',
  code: 'CZ-PRG'
}, {
  name: 'Dublin, Ireland',
  code: 'IE-DUB'
}, {
  name: 'Warsaw, Poland',
  code: 'PL-WAR'
},
// North America - Countries
{
  name: 'United States',
  code: 'US'
}, {
  name: 'Canada',
  code: 'CA'
}, {
  name: 'Mexico',
  code: 'MX'
},
// North America - Major Cities
{
  name: 'New York, USA',
  code: 'US-NYC'
}, {
  name: 'Los Angeles, USA',
  code: 'US-LAX'
}, {
  name: 'Chicago, USA',
  code: 'US-CHI'
}, {
  name: 'San Francisco, USA',
  code: 'US-SFO'
}, {
  name: 'Seattle, USA',
  code: 'US-SEA'
}, {
  name: 'Boston, USA',
  code: 'US-BOS'
}, {
  name: 'Miami, USA',
  code: 'US-MIA'
}, {
  name: 'Houston, USA',
  code: 'US-HOU'
}, {
  name: 'Dallas, USA',
  code: 'US-DAL'
}, {
  name: 'Atlanta, USA',
  code: 'US-ATL'
}, {
  name: 'Washington DC, USA',
  code: 'US-WAS'
}, {
  name: 'Toronto, Canada',
  code: 'CA-TOR'
}, {
  name: 'Vancouver, Canada',
  code: 'CA-VAN'
}, {
  name: 'Montreal, Canada',
  code: 'CA-MTL'
}, {
  name: 'Mexico City, Mexico',
  code: 'MX-MEX'
},
// Asia - Countries
{
  name: 'Japan',
  code: 'JP'
}, {
  name: 'China',
  code: 'CN'
}, {
  name: 'South Korea',
  code: 'KR'
}, {
  name: 'India',
  code: 'IN'
}, {
  name: 'Singapore',
  code: 'SG'
}, {
  name: 'Thailand',
  code: 'TH'
}, {
  name: 'Malaysia',
  code: 'MY'
}, {
  name: 'Indonesia',
  code: 'ID'
}, {
  name: 'Vietnam',
  code: 'VN'
}, {
  name: 'Philippines',
  code: 'PH'
},
// Asia - Major Cities
{
  name: 'Tokyo, Japan',
  code: 'JP-TYO'
}, {
  name: 'Osaka, Japan',
  code: 'JP-OSA'
}, {
  name: 'Beijing, China',
  code: 'CN-BEI'
}, {
  name: 'Shanghai, China',
  code: 'CN-SHA'
}, {
  name: 'Hong Kong',
  code: 'HK'
}, {
  name: 'Seoul, South Korea',
  code: 'KR-SEL'
}, {
  name: 'Mumbai, India',
  code: 'IN-BOM'
}, {
  name: 'Delhi, India',
  code: 'IN-DEL'
}, {
  name: 'Bangalore, India',
  code: 'IN-BLR'
}, {
  name: 'Singapore',
  code: 'SG-SIN'
}, {
  name: 'Bangkok, Thailand',
  code: 'TH-BKK'
}, {
  name: 'Kuala Lumpur, Malaysia',
  code: 'MY-KUL'
}, {
  name: 'Jakarta, Indonesia',
  code: 'ID-JKT'
}, {
  name: 'Manila, Philippines',
  code: 'PH-MNL'
}, {
  name: 'Ho Chi Minh City, Vietnam',
  code: 'VN-SGN'
},
// Middle East
{
  name: 'Dubai, UAE',
  code: 'AE-DXB'
}, {
  name: 'Abu Dhabi, UAE',
  code: 'AE-AUH'
}, {
  name: 'Tel Aviv, Israel',
  code: 'IL-TLV'
}, {
  name: 'Istanbul, Turkey',
  code: 'TR-IST'
}, {
  name: 'Riyadh, Saudi Arabia',
  code: 'SA-RUH'
},
// Oceania
{
  name: 'Australia',
  code: 'AU'
}, {
  name: 'New Zealand',
  code: 'NZ'
}, {
  name: 'Sydney, Australia',
  code: 'AU-SYD'
}, {
  name: 'Melbourne, Australia',
  code: 'AU-MEL'
}, {
  name: 'Brisbane, Australia',
  code: 'AU-BNE'
}, {
  name: 'Perth, Australia',
  code: 'AU-PER'
}, {
  name: 'Auckland, New Zealand',
  code: 'NZ-AKL'
},
// South America
{
  name: 'Brazil',
  code: 'BR'
}, {
  name: 'Argentina',
  code: 'AR'
}, {
  name: 'Chile',
  code: 'CL'
}, {
  name: 'São Paulo, Brazil',
  code: 'BR-SAO'
}, {
  name: 'Rio de Janeiro, Brazil',
  code: 'BR-RIO'
}, {
  name: 'Buenos Aires, Argentina',
  code: 'AR-BUE'
}, {
  name: 'Santiago, Chile',
  code: 'CL-SCL'
},
// Africa
{
  name: 'South Africa',
  code: 'ZA'
}, {
  name: 'Cape Town, South Africa',
  code: 'ZA-CPT'
}, {
  name: 'Johannesburg, South Africa',
  code: 'ZA-JNB'
}, {
  name: 'Cairo, Egypt',
  code: 'EG-CAI'
}, {
  name: 'Lagos, Nigeria',
  code: 'NG-LOS'
}];
const ZONE_MAP: Record<string, MarketZone> = {
  Germany: 'DE',
  Spain: 'ES',
  UK: 'UK',
  France: 'FR'
};
const timelineOptions = [{
  value: 24,
  label: 'Next 24 hours'
}, {
  value: 48,
  label: 'Next 2 days'
}, {
  value: 72,
  label: 'Next 3 days'
}, {
  value: 120,
  label: 'Next 5 days'
}, {
  value: 168,
  label: 'Next 7 days'
}];
interface ForecastViewProps {
  activeRegion: string;
}
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    // Get the full timestamp from the first payload item
    const dataPoint = payload[0]?.payload;
    const timestamp = dataPoint?.timestamp;
    // Format date and time
    const dateStr = timestamp ? new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) : '';
    const timeStr = timestamp ? new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }) : label;
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-50 rounded-xl p-4 shadow-2xl" data-id="element-1727">
        <div className="mb-3 pb-2 border-b border-slate-200" data-id="element-1728">
          <p className="text-xs font-semibold text-slate-900" data-id="element-1729">{dateStr}</p>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-1730">{timeStr}</p>
        </div>
        <div className="space-y-2" data-id="element-1731">
          {payload.map((entry: any) => <div key={entry.name} className="flex items-center justify-between gap-6" data-id="element-1732">
              <div className="flex items-center gap-2" data-id="element-1733">
                <div className="w-2.5 h-2.5 rounded-full shadow-sm" style={{
              backgroundColor: entry.color
            }} data-id="element-1734" />
                <span className="text-xs font-medium text-slate-700" data-id="element-1735">
                  {entry.name}
                </span>
              </div>
              <span className="text-sm font-bold text-slate-900 font-mono" data-id="element-1736">
                {entry.value.toFixed(1)}
              </span>
            </div>)}
        </div>
      </div>;
  }
  return null;
};
export function ForecastView({
  activeRegion
}: ForecastViewProps) {
  const navigate = useNavigate();
  const [forecasts, setForecasts] = useState<WeatherForecast[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedParameter, setSelectedParameter] = useState<'windSpeed' | 'temperature' | 'solarRadiation'>('windSpeed');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [showDataTooltip, setShowDataTooltip] = useState(false);
  const [timelineHours, setTimelineHours] = useState(48);
  const [showTimelineDropdown, setShowTimelineDropdown] = useState(false);
  // Location selector state
  const [selectedLocation, setSelectedLocation] = useState<LocationOption>({
    name: 'Germany',
    code: 'DE'
  });
  const [showLocationSettings, setShowLocationSettings] = useState(false);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [favoriteLocations, setFavoriteLocations] = useState<string[]>(['DE', 'UK']);
  // Sync selectedLocation with activeRegion prop
  useEffect(() => {
    const zone = ZONE_MAP[activeRegion];
    if (zone) {
      const location = availableLocations.find(loc => loc.code === zone);
      if (location && location.code !== selectedLocation.code) {
        setSelectedLocation(location);
      }
    }
  }, [activeRegion]);
  useEffect(() => {
    loadForecasts();
  }, [selectedLocation]);
  const loadForecasts = async () => {
    setLoading(true);
    const data = await fetchWeatherForecast(selectedLocation.code);
    setForecasts(data);
    setLastUpdated(new Date());
    setLoading(false);
  };
  const filteredLocations = availableLocations.filter(loc => loc.name.toLowerCase().includes(locationSearchQuery.toLowerCase()));
  const toggleFavorite = (code: MarketZone) => {
    setFavoriteLocations(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };
  if (loading) {
    return <div className="flex items-center justify-center h-full" data-id="element-1737">
        <div className="relative" data-id="element-1738">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500/20 border-t-pink-500" data-id="element-1739" />
          <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-pink-500/10 to-purple-500/10 blur-xl" data-id="element-1740" />
        </div>
      </div>;
  }
  // Group forecasts by model and prepare chart data
  const chartData: any[] = [];
  const timePoints = [...new Set(forecasts.map(f => f.timestamp.toISOString()))];
  timePoints.slice(0, timelineHours).forEach(time => {
    const timestamp = new Date(time);
    const point: any = {
      time: new Date(time).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }),
      fullTime: time,
      timestamp: timestamp,
      hour: timestamp.getHours(),
      date: timestamp.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      }),
      isNewDay: timestamp.getHours() === 0
    };
    forecasts.filter(f => f.timestamp.toISOString() === time).forEach(f => {
      point[f.model] = f.parameters[selectedParameter];
    });
    chartData.push(point);
  });
  const parameterLabels = {
    windSpeed: {
      label: 'Wind Speed',
      unit: 'm/s',
      gradient: 'from-cyan-500 to-blue-500'
    },
    temperature: {
      label: 'Temperature',
      unit: '°C',
      gradient: 'from-orange-500 to-red-500'
    },
    solarRadiation: {
      label: 'Solar Radiation',
      unit: 'W/m²',
      gradient: 'from-yellow-500 to-orange-500'
    }
  };
  const modelColors = {
    'EPT-2': '#ff006b',
    'EC-IFS': '#06b6d4',
    GFS: '#a855f7'
  };
  // Format tick label - show date and time from the data
  const formatXAxisTick = (timeValue: string) => {
    const dataPoint = chartData.find(d => d.time === timeValue);
    if (!dataPoint) return timeValue;
    return `${dataPoint.date}\n${dataPoint.time}`;
  };
  // Custom tick component - show date only when it changes, otherwise show time
  const CustomXAxisTick = (props: any) => {
    const {
      x,
      y,
      payload,
      index
    } = props;
    const dataPoint = chartData.find(d => d.time === payload.value);
    if (!dataPoint) return null;
    // Check if this is a new date compared to previous tick
    let showDate = false;
    if (index === 0) {
      // Always show date for first tick
      showDate = true;
    } else {
      // Check if date changed from previous data point
      const currentIndex = chartData.findIndex(d => d.time === payload.value);
      if (currentIndex > 0) {
        const prevDate = chartData[currentIndex - 1]?.date;
        showDate = prevDate !== dataPoint.date;
      }
    }
    return <g transform={`translate(${x},${y})`} data-id="element-1741">
        <text x={0} y={0} dy={16} textAnchor="middle" fill={showDate ? '#334155' : '#64748b'} fontSize={11} fontWeight={showDate ? 600 : 500} fontFamily={showDate ? 'inherit' : 'monospace'} data-id="element-1742">
          {showDate ? dataPoint.date : dataPoint.time}
        </text>
      </g>;
  };
  const selectedTimelineLabel = timelineOptions.find(opt => opt.value === timelineHours)?.label || 'Next 2 days';
  return <div className="space-y-6 relative" data-id="element-1743">
      {/* Header Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-1744">
        <div className="flex items-center gap-4" data-id="element-1745">
          <div data-id="element-1746">
            <h2 className="text-2xl font-bold text-slate-900" data-id="element-1747">
              Weather Forecast
            </h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-1748">
              Multi-model comparison • Real data from Open-Meteo API
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3" data-id="element-1749">
          {/* Location Selector */}
          <div className="relative" data-id="element-1750">
            <button onClick={() => setShowLocationSettings(!showLocationSettings)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/80 border border-slate-200 hover:border-pink-300 hover:bg-pink-50/50 text-slate-700 hover:text-pink-700 transition-all shadow-sm" data-id="element-1751">
              <MapPin size={16} data-id="element-1752" />
              <span className="text-sm font-medium" data-id="element-1753">
                {selectedLocation.name}
              </span>
              <ChevronDown size={14} className={`transition-transform ${showLocationSettings ? 'rotate-180' : ''}`} data-id="element-1754" />
            </button>

            {/* Location Settings Modal */}
            <AnimatePresence data-id="element-1755">
              {showLocationSettings && <>
                  {/* Backdrop */}
                  <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} onClick={() => setShowLocationSettings(false)} className="fixed inset-0 z-40" data-id="element-1756" />

                  {/* Dropdown */}
                  <motion.div initial={{
                opacity: 0,
                scale: 0.95,
                y: -10
              }} animate={{
                opacity: 1,
                scale: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95,
                y: -10
              }} className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden" data-id="element-1757">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50/50" data-id="element-1758">
                      <div data-id="element-1759">
                        <h3 className="text-sm font-bold text-slate-900" data-id="element-1760">
                          Select Location
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-1761">
                          {selectedLocation.name}
                        </p>
                      </div>
                      <button onClick={() => setShowLocationSettings(false)} className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-1762">
                        <X size={16} className="text-slate-500" data-id="element-1763" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-4 max-h-[500px] overflow-y-auto custom-scrollbar" data-id="element-1764">
                      {/* Search */}
                      <div className="relative mb-4" data-id="element-1765">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-1766" />
                        <input type="text" placeholder="Search location..." value={locationSearchQuery} onChange={e => setLocationSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-500/50" data-id="element-1767" />
                      </div>

                      {/* Favorites */}
                      {favoriteLocations.length > 0 && <div className="mb-4" data-id="element-1768">
                          <div className="flex items-center gap-2 mb-2" data-id="element-1769">
                            <Star size={12} className="text-pink-500 fill-pink-500" data-id="element-1770" />
                            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider" data-id="element-1771">
                              Favorites
                            </p>
                          </div>
                          <div className="space-y-1" data-id="element-1772">
                            {availableLocations.filter(loc => favoriteLocations.includes(loc.code)).map(location => <button key={location.code} onClick={() => {
                        setSelectedLocation(location);
                        setShowLocationSettings(false);
                      }} className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors group" data-id="element-1773">
                                  <div className="flex items-center gap-2" data-id="element-1774">
                                    <MapPin size={14} className="text-slate-400" data-id="element-1775" />
                                    <span className="text-sm font-medium text-slate-900" data-id="element-1776">
                                      {location.name}
                                    </span>
                                  </div>
                                  <button onClick={e => {
                          e.stopPropagation();
                          toggleFavorite(location.code);
                        }} className="p-1 hover:bg-slate-100 rounded transition-colors" data-id="element-1777">
                                    <Star size={14} className="text-pink-500 fill-pink-500" data-id="element-1778" />
                                  </button>
                                </button>)}
                          </div>
                        </div>}

                      {/* All Locations */}
                      <div data-id="element-1779">
                        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2" data-id="element-1780">
                          All Locations
                        </p>
                        <div className="space-y-1" data-id="element-1781">
                          {filteredLocations.map(location => {
                        const isFavorite = favoriteLocations.includes(location.code);
                        const isSelected = selectedLocation.code === location.code;
                        return <button key={location.code} onClick={() => {
                          setSelectedLocation(location);
                          setShowLocationSettings(false);
                        }} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors group ${isSelected ? 'bg-pink-50 border border-pink-200' : 'hover:bg-slate-50'}`} data-id="element-1782">
                                <div className="flex items-center gap-2" data-id="element-1783">
                                  <MapPin size={14} className={isSelected ? 'text-pink-600' : 'text-slate-400'} data-id="element-1784" />
                                  <span className={`text-sm font-medium ${isSelected ? 'text-pink-900' : 'text-slate-900'}`} data-id="element-1785">
                                    {location.name}
                                  </span>
                                </div>
                                <button onClick={e => {
                            e.stopPropagation();
                            toggleFavorite(location.code);
                          }} className="p-1 hover:bg-slate-100 rounded transition-colors" data-id="element-1786">
                                  <Star size={14} className={isFavorite ? 'text-pink-500 fill-pink-500' : 'text-slate-300'} data-id="element-1787" />
                                </button>
                              </button>;
                      })}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </>}
            </AnimatePresence>
          </div>

          <button onClick={loadForecasts} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/60 border border-slate-50 hover:bg-slate-50 hover:border-slate-300 text-slate-700 transition-all shadow-sm hover:shadow" data-id="element-1788">
            <RefreshCw size={16} data-id="element-1789" />
            <span className="hidden sm:inline" data-id="element-1790">Refresh</span>
          </button>
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:shadow-lg hover:shadow-pink-500/30 transition-all font-medium" data-id="element-1791">
            <Download size={16} data-id="element-1792" />
            <span className="hidden sm:inline" data-id="element-1793">Export</span>
          </button>
        </div>
      </div>

      {/* Parameter Selection */}
      <div className="flex items-center gap-3 flex-wrap" data-id="element-1794">
        <span className="text-sm font-semibold text-slate-700" data-id="element-1795">Parameter:</span>
        {(Object.keys(parameterLabels) as Array<keyof typeof parameterLabels>).map(param => <motion.button key={param} onClick={() => setSelectedParameter(param)} whileTap={{
        scale: 0.95
      }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedParameter === param ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg shadow-pink-500/30' : 'bg-white/60 border border-slate-50 text-slate-700 hover:bg-slate-50 hover:border-slate-300 hover:shadow-sm'}`} data-id="element-1796">
            {parameterLabels[param].label}
          </motion.button>)}
      </div>

      {/* Forecast Chart */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-6 shadow-lg shadow-slate-200/50 relative" data-id="element-1797">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02] rounded-xl overflow-hidden" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgb(0 0 0) 1px, transparent 0)',
        backgroundSize: '24px 24px'
      }} data-id="element-1798" />

        <div className="flex items-center justify-between mb-6 relative z-10" data-id="element-1799">
          <div data-id="element-1800">
            <h3 className="text-lg font-semibold text-slate-900" data-id="element-1801">
              {parameterLabels[selectedParameter].label} - {timelineHours} Hour
              Forecast
            </h3>
            <p className="text-xs text-slate-500 mt-1" data-id="element-1802">
              Comparing EPT-2, EC-IFS (real), and GFS models
            </p>
          </div>

          {/* Timeline Dropdown */}
          <div className="relative z-50" data-id="element-1803">
            <button onClick={() => setShowTimelineDropdown(!showTimelineDropdown)} className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50/80 px-3 py-1.5 rounded-lg border border-slate-200/50 hover:bg-slate-100 hover:border-slate-300 transition-colors" data-id="element-1804">
              <Calendar size={14} data-id="element-1805" />
              {selectedTimelineLabel}
              <ChevronDown size={14} className={`transition-transform ${showTimelineDropdown ? 'rotate-180' : ''}`} data-id="element-1806" />
            </button>

            {/* Dropdown Menu */}
            {showTimelineDropdown && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="absolute right-0 top-full mt-2 w-48 bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl shadow-xl shadow-slate-900/10 overflow-hidden" data-id="element-1807">
                {timelineOptions.map(option => <button key={option.value} onClick={() => {
              setTimelineHours(option.value);
              setShowTimelineDropdown(false);
            }} className={`w-full text-left px-4 py-2.5 text-xs transition-colors ${timelineHours === option.value ? 'bg-pink-50 text-pink-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-1808">
                    {option.label}
                  </button>)}
              </motion.div>}
          </div>
        </div>

        <div className="h-[450px] relative z-0 pb-10 overflow-hidden" data-id="element-1809">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1810">
            <AreaChart data={chartData} margin={{
            top: 10,
            right: 30,
            left: 0,
            bottom: 0
          }} data-id="element-1811">
              <defs data-id="element-1812">
                <linearGradient id="gradientEPT2" x1="0" y1="0" x2="0" y2="1" data-id="element-1813">
                  <stop offset="5%" stopColor="#ff006b" stopOpacity={0.15} data-id="element-1814" />
                  <stop offset="95%" stopColor="#ff006b" stopOpacity={0} data-id="element-1815" />
                </linearGradient>
                <linearGradient id="gradientECIFS" x1="0" y1="0" x2="0" y2="1" data-id="element-1816">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.15} data-id="element-1817" />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} data-id="element-1818" />
                </linearGradient>
                <linearGradient id="gradientGFS" x1="0" y1="0" x2="0" y2="1" data-id="element-1819">
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.15} data-id="element-1820" />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0} data-id="element-1821" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} strokeWidth={1} data-id="element-1822" />
              <XAxis dataKey="time" stroke="#cbd5e1" tick={<CustomXAxisTick data-id="element-1824" />} tickLine={false} axisLine={{
              stroke: '#e2e8f0',
              strokeWidth: 1
            }} interval="preserveStartEnd" minTickGap={50} height={60} data-id="element-1823" />
              <YAxis stroke="#cbd5e1" tick={{
              fill: '#64748b',
              fontSize: 11,
              fontWeight: 500,
              fontFamily: 'monospace'
            }} tickLine={false} axisLine={{
              stroke: '#e2e8f0',
              strokeWidth: 1
            }} dx={-8} label={{
              value: parameterLabels[selectedParameter].unit,
              angle: -90,
              position: 'insideLeft',
              style: {
                fill: '#64748b',
                fontSize: 12,
                fontWeight: 600
              },
              offset: 10
            }} data-id="element-1825" />
              <Tooltip content={<CustomTooltip data-id="element-1827" />} cursor={{
              stroke: '#ff006b',
              strokeWidth: 1,
              strokeDasharray: '4 4'
            }} data-id="element-1826" />
              <Legend verticalAlign="top" height={36} iconType="circle" wrapperStyle={{
              fontSize: '12px',
              color: '#64748b'
            }} data-id="element-1828" />
              <Area type="monotone" dataKey="EPT-2" stroke="#ff006b" strokeWidth={3} fill="url(#gradientEPT2)" name="EPT-2" animationDuration={2000} dot={false} data-id="element-1829" />
              <Area type="monotone" dataKey="EC-IFS" stroke="#06b6d4" strokeWidth={2.5} fill="url(#gradientECIFS)" name="EC-IFS" animationDuration={2000} animationBegin={200} dot={false} data-id="element-1830" />
              <Area type="monotone" dataKey="GFS" stroke="#a855f7" strokeWidth={2.5} fill="url(#gradientGFS)" name="GFS" animationDuration={2000} animationBegin={400} dot={false} data-id="element-1831" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Data source badge with tooltip - positioned outside chart area with higher z-index */}
        <div className="relative z-50 flex justify-end -mt-6" data-id="element-1832">
          <div className="relative" onMouseEnter={() => setShowDataTooltip(true)} onMouseLeave={() => setShowDataTooltip(false)} data-id="element-1833">
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-slate-200/50 shadow-sm cursor-help" data-id="element-1834">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-1835" />
              Live data from Open-Meteo
            </div>

            {/* Tooltip */}
            {showDataTooltip && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="absolute right-0 bottom-full mb-2 w-72 bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-4 shadow-xl shadow-slate-900/10 z-[100]" data-id="element-1836">
                <div className="space-y-3" data-id="element-1837">
                  <div data-id="element-1838">
                    <p className="text-xs font-semibold text-slate-900 mb-1" data-id="element-1839">
                      Data Source
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-1840">
                      Open-Meteo Weather API
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5 font-mono" data-id="element-1841">
                      api.open-meteo.com/v1/forecast
                    </p>
                  </div>

                  <div className="border-t border-slate-200 pt-3" data-id="element-1842">
                    <p className="text-xs font-semibold text-slate-900 mb-1" data-id="element-1843">
                      Last Updated
                    </p>
                    <p className="text-xs text-slate-600" data-id="element-1844">
                      {lastUpdated.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-1845">
                      {lastUpdated.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                    </p>
                  </div>

                  <div className="border-t border-slate-200 pt-3" data-id="element-1846">
                    <p className="text-xs font-semibold text-slate-900 mb-1" data-id="element-1847">
                      Models
                    </p>
                    <div className="space-y-1" data-id="element-1848">
                      <div className="flex items-center gap-2" data-id="element-1849">
                        <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" data-id="element-1850" />
                        <p className="text-xs text-slate-600" data-id="element-1851">
                          ECMWF IFS (real)
                        </p>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-1852">
                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500" data-id="element-1853" />
                        <p className="text-xs text-slate-600" data-id="element-1854">GFS (real)</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-200 pt-3" data-id="element-1855">
                    <div className="flex items-center gap-2" data-id="element-1856">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-1857" />
                      <p className="text-xs font-medium text-green-700" data-id="element-1858">
                        Live Connection Active
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>}
          </div>
        </div>
      </motion.div>

      {/* Model Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1859">
        {Object.entries(modelColors).map(([model, color], idx) => {
        const modelForecasts = forecasts.filter(f => f.model === model).slice(0, 48);
        const values = modelForecasts.map(f => f.parameters[selectedParameter]);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        const max = Math.max(...values);
        const min = Math.min(...values);
        const trend = values[values.length - 1] > values[0];
        return <motion.div key={model} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: idx * 0.1
        }} className="bg-white/60 backdrop-blur-xl border border-slate-50 rounded-xl p-5 shadow-sm shadow-slate-200/50 hover:shadow-md hover:shadow-slate-200/60 transition-all" data-id="element-1860">
              <div className="flex items-center justify-between mb-4" data-id="element-1861">
                <div className="flex items-center gap-3" data-id="element-1862">
                  <div className="w-3 h-3 rounded-full shadow-lg" style={{
                backgroundColor: color,
                boxShadow: `0 0 10px ${color}40`
              }} data-id="element-1863" />
                  <h4 className="font-semibold text-slate-900" data-id="element-1864">{model}</h4>
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${trend ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`} data-id="element-1865">
                  {trend ? <TrendingUp size={12} data-id="element-1866" /> : <TrendingDown size={12} data-id="element-1867" />}
                  {trend ? 'Rising' : 'Falling'}
                </div>
              </div>
              <div className="space-y-3" data-id="element-1868">
                <div data-id="element-1869">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-1870">Average</p>
                  <p className="text-2xl font-bold text-slate-900 font-mono" data-id="element-1871">
                    {avg.toFixed(1)}
                    <span className="text-sm text-slate-400 ml-1 font-sans" data-id="element-1872">
                      {parameterLabels[selectedParameter].unit}
                    </span>
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-200/50" data-id="element-1873">
                  <div data-id="element-1874">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-1875">Min</p>
                    <p className="text-sm font-bold text-slate-700 font-mono" data-id="element-1876">
                      {min.toFixed(1)}
                    </p>
                  </div>
                  <div data-id="element-1877">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-1878">Max</p>
                    <p className="text-sm font-bold text-slate-700 font-mono" data-id="element-1879">
                      {max.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Concept Button - Bottom Right - Extremely Subtle */}
      <motion.button initial={{
      opacity: 0
    }} animate={{
      opacity: 0.15
    }} whileHover={{
      opacity: 0.6,
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} onClick={() => navigate('/forecast/concept')} className="fixed bottom-8 right-8 z-50 px-2 py-1 text-[10px] text-slate-400 hover:text-slate-600 transition-all font-normal tracking-wide" data-id="element-1880">
        Concept
      </motion.button>
    </div>;
}